import { PayloadAction } from '@reduxjs/toolkit'
import { Client } from 'api/client'
import {
  confirmConfiguration,
  confirmGmailSmtpConfiguration,
  confirmOfficeSmtpConfiguration,
  deleteConfiguration,
  getConfiguration,
  initConfiguration,
  syncInbox,
} from 'Modules/Communication/shared/api/inbox.api'
import { EmailProviderEnum } from 'Modules/Communication/shared/models/EmailConfig'
import { put, select, takeEvery, call } from 'redux-saga/effects'
import { getActiveClient } from 'shared/store/clients/selectors'
import { closeModal } from 'shared/store/modal'
import {
  fetchEmailConfigurationRequest,
  fetchEmailConfigurationSuccess,
  fetchEmailConfigurationError,
  initFetchEmailConfigurationError,
  initFetchEmailConfigurationRequest,
  deleteEmailConfigurationSuccess,
  deleteEmailConfigurationError,
  confirmEmailConfigurationRequested,
  deleteEmailConfigurationRequested,
  confirmEmailConfigurationSuccess,
  confirmEmailConfigurationError,
  syncEmailConfigurationRequested,
  syncEmailConfigurationSuccess,
  syncEmailConfigurationError,
  confirmSmtpConfigurationRequested,
  ConfirmSmtpConfigurationSuccess,
  confirmSmtpConfigurationError,
  syncSmtpRequested,
} from '.'

function* fetchConfig() {
  try {
    const client: Client = yield select(getActiveClient)
    const { data, status } = yield call(getConfiguration, client.id)

    if (status === 204) {
      yield put(fetchEmailConfigurationSuccess(null))
      return
    }
    yield put(fetchEmailConfigurationSuccess(data))
  } catch (e: any) {
    if (e.response.status === 404) {
      yield put(fetchEmailConfigurationSuccess(null))
      return
    }

    console.log(e.response.data)
    yield put(fetchEmailConfigurationError(e))
  }
}

function* initConfig({
  payload,
}: PayloadAction<
  { provider: EmailProviderEnum; returnUrl: string; email: string },
  string
>) {
  try {
    const client: Client = yield select(getActiveClient)
    const { data } = yield call(
      initConfiguration,
      client.id,
      payload.provider,
      payload.returnUrl,
      payload.email
    )
    window.location = data
  } catch (e: any) {
    console.log(e.response.data)
    yield put(initFetchEmailConfigurationError())
  }
}

function* confirmConfig({
  payload,
}: PayloadAction<{
  provider: EmailProviderEnum
  email: string
  syncFromDate: Date
}>) {
  try {
    const client: Client = yield select(getActiveClient)
    yield call(
      confirmConfiguration,
      client.id,
      payload.provider,
      payload.email,
      payload.syncFromDate
    )

    yield put(confirmEmailConfigurationSuccess())
    yield put(closeModal())
    yield put(fetchEmailConfigurationRequest())
  } catch (e: any) {
    console.log(e.response?.data)
    yield put(confirmEmailConfigurationError())
  }
}

function* deleteConfig() {
  try {
    const client: Client = yield select(getActiveClient)

    yield call(deleteConfiguration, client.id)
    yield put(deleteEmailConfigurationSuccess())
    yield put(closeModal())

    yield put(fetchEmailConfigurationRequest())
  } catch (e: any) {
    console.log(e.response.data)
    yield put(deleteEmailConfigurationError())
  }
}

function* syncInboxConfig({
  payload,
}: PayloadAction<{
  provider: EmailProviderEnum
  syncFromDate: Date
}>) {
  try {
    const client: Client = yield select(getActiveClient)
    yield call(syncInbox, client.id, payload.syncFromDate, payload.provider)

    yield put(closeModal())
    yield put(syncEmailConfigurationSuccess())
  } catch (e: any) {
    console.log(e.response.data)
    yield put(syncEmailConfigurationError())
  }
}

function* confirmSmtpConfiguration({
  payload,
}: PayloadAction<{
  email: string
  password: string
  syncFromDate: Date
  provider: EmailProviderEnum
}>) {
  try {
    const client: Client = yield select(getActiveClient)
    if (payload.provider === EmailProviderEnum.gmail) {
      yield call(
        confirmGmailSmtpConfiguration,
        client.id,
        payload.email,
        payload.password,
        payload.syncFromDate
      )
    } else {
      yield call(
        confirmOfficeSmtpConfiguration,
        client.id,
        payload.email,
        payload.password,
        payload.syncFromDate
      )
    }

    yield put(ConfirmSmtpConfigurationSuccess())
    yield put(closeModal())
    yield put(fetchEmailConfigurationRequest())
  } catch (e: any) {
    console.error(e)
    yield put(confirmSmtpConfigurationError())
  }
}

function* syncSmtpConnection({
  payload,
}: PayloadAction<{ syncFromDate: Date; emailProvider: EmailProviderEnum }>) {
  try {
    const client: Client = yield select(getActiveClient)
    yield call(
      syncInbox,
      client.id,
      payload.syncFromDate,
      payload.emailProvider
    )

    yield put(syncEmailConfigurationSuccess())
  } catch (e: any) {
    yield put(syncEmailConfigurationError())
  }
}

function* emailsConfigurationSaga() {
  yield takeEvery(fetchEmailConfigurationRequest.type, fetchConfig)
  yield takeEvery(initFetchEmailConfigurationRequest.type, initConfig)
  yield takeEvery(confirmEmailConfigurationRequested.type, confirmConfig)
  yield takeEvery(deleteEmailConfigurationRequested.type, deleteConfig)
  yield takeEvery(syncEmailConfigurationRequested.type, syncInboxConfig)

  yield takeEvery(
    confirmSmtpConfigurationRequested.type,
    confirmSmtpConfiguration
  )
  yield takeEvery(syncSmtpRequested.type, syncSmtpConnection)
}

export default emailsConfigurationSaga
